import React, { useEffect, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GroupIcon from '@material-ui/icons/Group';
import PeopleIcon from '@material-ui/icons/People';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import SendIcon from '@material-ui/icons/Send';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import FiltrosTabela from '../../Components/FiltrosTabela';
import { api, getListAPI } from '../../services';
import { PRIMARY_COLOR, STATUS_ATIVO } from '../../utils/constants';
import {
  CAMPOS_FILTRO_COLABORADOR,
  TextoDoModelo,
  VisualizadorPdf,
  FILTROS_COLABORADOR_VAZIOS,
  filtrosDaLista,
  mensagemDoErro,
} from './DialogoCreateEdit';

// Na geração não entram os filtros de usuário, gestor, local, grupo e funcionário interno.
const CAMPOS_FILTRO_GERACAO = [
  ...CAMPOS_FILTRO_COLABORADOR.filter(
    (campo) =>
      !['is_user', 'is_gestor', 'local', 'grupo', 'interno'].includes(
        campo.chave,
      ),
  ),
  {
    chave: 'sexo',
    rotulo: 'Sexo',
    tipo: 'selecao',
    opcoes: [
      { id: 'masculino', nome: 'Masculino' },
      { id: 'feminino', nome: 'Feminino' },
    ],
  },
];

// Para quem gerar: opções do seletor (cartões que se escolhem como botões de rádio).
const OPCOES_DESTINO = [
  {
    valor: 'um',
    titulo: 'Um colaborador',
    descricao: 'Escolha uma pessoa',
    Icone: PersonOutlineIcon,
  },
  {
    valor: 'varios',
    titulo: 'Vários',
    descricao: 'Por filtros, como função e loja',
    Icone: GroupIcon,
  },
  {
    valor: 'todos',
    titulo: 'Todos os ativos',
    descricao: 'Todos os colaboradores ativos',
    Icone: PeopleIcon,
  },
];

const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const LIMITE_VALOR = 1000;

// Valor digitado -> como entra no documento e no servidor (data dd/mm/aaaa, número com vírgula).
const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
// "12/10/2026" -> "12 de outubro de 2026" (só na prévia da tela: o servidor faz o mesmo no PDF).
const porExtenso = (valor) => {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valor);
  return partes
    ? `${Number(partes[1])} de ${MESES[Number(partes[2]) - 1]} de ${partes[3]}`
    : valor;
};

const formatarValor = (tipo, valor) => {
  if (!valor) return '';
  if (tipo === 'data') return valor.split('-').reverse().join('/');
  if (tipo === 'numero') return valor.replace('.', ',');
  return valor;
};

const ROTULO_ACAO_EXPLICADA = {
  assinar: 'Assinar o documento',
  visualizar: 'Ler e marcar como lido (sem assinatura)',
  enviar_arquivo: 'Enviar um arquivo',
};
const ROTULO_RECORRENCIA = {
  unica: 'Única',
  sob_demanda: 'Sob demanda',
  mensal: 'Mensal',
  anual: 'Anual',
};

const ListaRelatorio = ({ titulo, itens, severity }) =>
  itens.length > 0 ? (
    <Alert severity={severity} style={{ marginTop: 12 }}>
      <Typography variant="subtitle2">
        {titulo} ({itens.length})
      </Typography>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {itens.map((item) => (
          <Typography variant="body2" key={`${titulo}-${item.colaborador_id}`}>
            {item.nome || `Colaborador ${item.colaborador_id}`}
            {item.motivo ? `: ${item.motivo}` : ''}
          </Typography>
        ))}
      </div>
    </Alert>
  ) : null;

// Dado do modelo com rótulo explícito.
const DadoModelo = ({ rotulo, children }) => (
  <div>
    <Typography variant="caption" color="textSecondary" display="block">
      {rotulo}
    </Typography>
    {typeof children === 'string' ? (
      <Typography variant="body2" style={{ fontWeight: 500 }}>
        {children}
      </Typography>
    ) : (
      children
    )}
  </div>
);

// Linha com ícone, título e um controle à direita (switch).
const OpcaoLinha = ({ icone, titulo, descricao, controle }) => (
  <Paper
    variant="outlined"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 12px',
    }}
  >
    {icone}
    <Box flex={1} minWidth={0}>
      <Typography variant="subtitle2">{titulo}</Typography>
      {descricao && (
        <Typography variant="caption" color="textSecondary">
          {descricao}
        </Typography>
      )}
    </Box>
    {controle}
  </Paper>
);

// Bolinhas numeradas ligadas por uma linha: as feitas ficam com ✓.
const EtapasVariaveis = ({ total, atual }) => (
  <Box display="flex" alignItems="center" mb={3}>
    {Array.from({ length: total }, (_, indice) => (
      <React.Fragment key={indice}>
        {indice > 0 && (
          <div
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              margin: '0 8px',
              backgroundColor: indice <= atual ? PRIMARY_COLOR : '#d5dbe7',
            }}
          />
        )}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            color: indice <= atual ? 'white' : '#7b8499',
            backgroundColor: indice <= atual ? PRIMARY_COLOR : '#e3e7ef',
            boxShadow:
              indice === atual ? `0 0 0 4px ${PRIMARY_COLOR}33` : 'none',
          }}
        >
          {indice < atual ? <CheckIcon style={{ fontSize: 18 }} /> : indice + 1}
        </div>
      </React.Fragment>
    ))}
  </Box>
);

// `colaboradorFixo` e `competencia`: geração de um colaborador, a partir da conferência do
// detalhe do modelo (por colaborador).
const DialogoGerarDocumentos = ({
  open,
  modelo,
  onClose,
  colaboradorFixo,
  competencia,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const celular = useMediaQuery((tema) => tema.breakpoints.down('sm'));
  const [abrangencia, setAbrangencia] = useState('varios');
  const [filtros, setFiltros] = useState(FILTROS_COLABORADOR_VAZIOS);
  const todos = abrangencia === 'todos';
  const [colaboradores, setColaboradores] = useState([]);
  const [opcoes, setOpcoes] = useState([]);
  const [termo, setTermo] = useState('');
  const [gerando, setGerando] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [pendentes, setPendentes] = useState([]);
  const [trocarPdf, setTrocarPdf] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  // Variáveis do modelo: o valor é pedido uma por vez antes de gerar.
  const [etapaVar, setEtapaVar] = useState(null);
  const [valores, setValores] = useState({});
  const [previaUrl, setPreviaUrl] = useState(null);
  const [gerandoPrevia, setGerandoPrevia] = useState(false);
  // Ignora cliques repetidos antes de o estado `gerando` refletir na tela.
  const gerandoRef = useRef(false);

  const temFiltro =
    abrangencia === 'varios' && Object.keys(filtrosDaLista(filtros)).length > 0;

  const escolheDestinatarios =
    Boolean(modelo) &&
    (Boolean(colaboradorFixo) ||
      modelo.recorrencia === 'sob_demanda' ||
      (modelo.recorrencia === 'unica' && !modelo.abrangencia));

  // Troca de PDF: só modelo único (assinar/visualizar) e exatamente 1
  // colaborador. O servidor valida a mesma regra.
  const podeTrocarPdf =
    escolheDestinatarios &&
    modelo.acao !== 'enviar_arquivo' &&
    modelo.origem !== 'texto' &&
    abrangencia === 'um' &&
    colaboradores.length === 1;

  let variaveis = [];
  try {
    variaveis = JSON.parse((modelo && modelo.variaveis_personalizadas) || '[]');
  } catch (e) {
    variaveis = [];
  }
  // Com outro PDF no lugar do modelo o texto não é usado, então não há o que perguntar.
  const precisaVariaveis = variaveis.length > 0 && !(trocarPdf && arquivo);
  const valoresFormatados = Object.fromEntries(
    variaveis.map(({ chave, tipo }) => [
      chave,
      formatarValor(tipo, valores[chave]),
    ]),
  );
  // Texto da prévia ao lado: data "por extenso" já como vai aparecer no documento.
  const valoresNaPrevia = Object.fromEntries(
    variaveis.map(({ chave, tipo, formato }) => [
      chave,
      tipo === 'data' && formato === 'extenso'
        ? porExtenso(valoresFormatados[chave])
        : valoresFormatados[chave],
    ]),
  );
  // Com variáveis o valor é de um colaborador só: gera um por vez.
  // Um arquivo por colaborador (sob demanda): o arquivo do colaborador é obrigatório.
  const arquivoObrigatorio =
    Boolean(modelo) && modelo.origem === 'por_colaborador';
  const soUm =
    variaveis.length > 0 || Boolean(colaboradorFixo) || arquivoObrigatorio;

  useEffect(() => {
    if (!open || !soUm) return;
    setAbrangencia('um');
    setColaboradores(colaboradorFixo ? [colaboradorFixo] : []);
  }, [open, soUm, colaboradorFixo && colaboradorFixo.id]);

  useEffect(() => {
    if (!podeTrocarPdf) {
      setTrocarPdf(false);
      setArquivo(null);
    } else if (arquivoObrigatorio) setTrocarPdf(true);
  }, [podeTrocarPdf, arquivoObrigatorio]);

  useEffect(() => {
    if (!open || !escolheDestinatarios) return;
    getListAPI(
      'colaboradores',
      ['nome', 'asc'],
      [1, 10],
      { nome: termo, status: STATUS_ATIVO },
      [],
    ).then(({ data }) => setOpcoes(data));
  }, [open, termo, escolheDestinatarios]);

  // Documentos pendentes deste modelo (só aviso: gerar de novo é permitido).
  useEffect(() => {
    setPendentes([]);
    // Só o tipo sob demanda permite gerar de novo; no único o servidor recusa.
    if (!open || !modelo || modelo.recorrencia !== 'sob_demanda') return;
    getListAPI(
      'documentos',
      ['id', 'desc'],
      [1, 100],
      { modelo_documento_id: modelo.id, status: 'pendente' },
      [],
    )
      .then(({ data }) => setPendentes(data))
      .catch(() => setPendentes([]));
  }, [open, modelo && modelo.id, modelo && modelo.recorrencia, relatorio]);

  // Colaboradores do destino atual que já têm documento pendente deste modelo.
  const idsDestino = colaboradores.map((c) => c.id);
  const comPendencia = [
    ...new Map(
      pendentes
        .filter((d) => todos || idsDestino.includes(d.colaborador_id))
        .map((d) => [
          d.colaborador_id,
          d.colaborador
            ? d.colaborador.nome
            : `Colaborador ${d.colaborador_id}`,
        ]),
    ).values(),
  ];

  function fechar() {
    setAbrangencia(soUm ? 'um' : 'varios');
    setFiltros(FILTROS_COLABORADOR_VAZIOS);
    setColaboradores(colaboradorFixo ? [colaboradorFixo] : []);
    setRelatorio(null);
    setTrocarPdf(false);
    setArquivo(null);
    setEtapaVar(null);
    setValores({});
    onClose();
  }

  async function handleGerar() {
    if (gerandoRef.current) return;
    gerandoRef.current = true;
    setGerando(true);
    try {
      const ids =
        abrangencia === 'um'
          ? colaboradores.map((colaborador) => colaborador.id)
          : [];
      let response;
      if (trocarPdf && arquivo) {
        const formData = new FormData();
        formData.append('todos', 'false');
        formData.append('colaboradores_ids', JSON.stringify(ids));
        formData.append('pdf_substituto', arquivo);
        response = await api.post(
          `/modelos_documentos/${modelo.id}/gerar`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      } else {
        response = await api.post(`/modelos_documentos/${modelo.id}/gerar`, {
          todos,
          colaboradores_ids: ids,
          ...(abrangencia === 'varios' && temFiltro
            ? { filtro_colaboradores: filtrosDaLista(filtros) }
            : {}),
          ...(precisaVariaveis ? { valores_variaveis: valoresFormatados } : {}),
          ...(competencia ? { competencia } : {}),
        });
      }
      const { status, message, data } = response.data;
      if (status) setRelatorio(data);
      else enqueueSnackbar(message, { variant: 'error' });
    } catch (e) {
      const mensagem =
        e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao gerar documentos.';
      enqueueSnackbar(mensagem, { variant: 'error' });
    } finally {
      gerandoRef.current = false;
      setGerando(false);
      setEtapaVar(null);
    }
  }

  // PDF do texto com o que já foi digitado (nada é gravado). Com um colaborador escolhido
  // usa os dados dele; senão, dados de exemplo.
  async function handlePrevia() {
    setGerandoPrevia(true);
    try {
      const response = await api.post(
        '/modelos_documentos/preview',
        {
          nome: modelo.nome,
          recorrencia: modelo.recorrencia,
          conteudo_texto: modelo.conteudo_texto,
          orientacao: modelo.orientacao,
          variaveis_personalizadas: variaveis,
          valores_variaveis: valoresFormatados,
          ...(colaboradores.length === 1
            ? { colaborador_id: colaboradores[0].id }
            : {}),
        },
        { responseType: 'blob' },
      );
      setPreviaUrl(URL.createObjectURL(response.data));
    } catch (e) {
      enqueueSnackbar(await mensagemDoErro(e), { variant: 'error' });
    } finally {
      setGerandoPrevia(false);
    }
  }

  function fecharPrevia() {
    if (previaUrl) URL.revokeObjectURL(previaUrl);
    setPreviaUrl(null);
  }

  function handleArquivo(event) {
    const [selecionado] = event.target.files;
    if (!selecionado) return;
    if (
      !['application/pdf', 'image/jpeg', 'image/png'].includes(selecionado.type)
    ) {
      enqueueSnackbar('Selecione um PDF ou uma imagem JPG/PNG.', {
        variant: 'error',
      });
      return;
    }
    if (selecionado.size > TAMANHO_MAXIMO) {
      enqueueSnackbar('O arquivo deve ter no máximo 10 MB.', {
        variant: 'error',
      });
      return;
    }
    setArquivo(selecionado);
  }

  const semDestinatario =
    escolheDestinatarios &&
    ((abrangencia === 'um' && colaboradores.length === 0) ||
      (abrangencia === 'varios' && !temFiltro));
  const comPrevia =
    etapaVar !== null &&
    !relatorio &&
    Boolean(modelo) &&
    modelo.origem === 'texto' &&
    // Celular: sem o texto ao lado (não cabe); o botão "Prévia" mostra o PDF.
    !celular;
  const faltaArquivo = (trocarPdf || arquivoObrigatorio) && !arquivo;
  const ultimaEtapa = etapaVar !== null && etapaVar === variaveis.length - 1;
  let rotuloBotao = 'Gerar';
  if (etapaVar === null && precisaVariaveis) rotuloBotao = 'Continuar';
  else if (etapaVar !== null && !ultimaEtapa) rotuloBotao = 'Próxima';

  return (
    <Dialog
      open={open}
      onClose={fechar}
      fullWidth
      fullScreen={celular}
      maxWidth={comPrevia ? 'lg' : 'sm'}
    >
      <Box
        display="flex"
        alignItems="center"
        px={2}
        py={1}
        style={{ backgroundColor: PRIMARY_COLOR, color: 'white' }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Gerar documentos
        </Typography>
        <Box flex={1} />
        <IconButton
          size="small"
          onClick={fechar}
          disabled={gerando}
          style={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent style={celular ? { padding: '12px 16px' } : undefined}>
        {modelo && (etapaVar === null || relatorio) && (
          <Paper
            variant="outlined"
            style={{ padding: celular ? 12 : 16, marginBottom: 16 }}
          >
            <DadoModelo rotulo="Modelo">
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {modelo.nome}
              </Typography>
            </DadoModelo>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 16,
                marginTop: 12,
              }}
            >
              <DadoModelo rotulo="Ação">
                {ROTULO_ACAO_EXPLICADA[modelo.acao]}
              </DadoModelo>
              {modelo.acao === 'assinar' && (
                <DadoModelo rotulo="Quem assina">
                  {modelo.mais_assinaturas
                    ? `Colaborador e mais ${
                        (modelo.assinantesAdicionais || []).length
                      } pessoa(s) fixa(s)`
                    : null}
                  {!modelo.mais_assinaturas &&
                  modelo.quem_assina === 'usuario_fixo'
                    ? `Pessoa fixa${
                        modelo.usuarioFixo
                          ? `: ${
                              (modelo.usuarioFixo.colaborador &&
                                modelo.usuarioFixo.colaborador.nome) ||
                              modelo.usuarioFixo.username
                            }`
                          : ''
                      }`
                    : null}
                  {!modelo.mais_assinaturas &&
                  modelo.quem_assina !== 'usuario_fixo'
                    ? 'O próprio colaborador'
                    : null}
                </DadoModelo>
              )}
              <DadoModelo rotulo="Recorrência">
                {ROTULO_RECORRENCIA[modelo.recorrencia]}
              </DadoModelo>
            </div>
          </Paper>
        )}
        {etapaVar !== null && !relatorio ? (
          <Box
            pt={1}
            display="flex"
            flexDirection={celular ? 'column' : 'row'}
            style={{ gap: 24 }}
          >
            <Box flex={1} minWidth={280}>
              <EtapasVariaveis total={variaveis.length} atual={etapaVar} />
              <Typography variant="caption" color="textSecondary">
                {`Variável ${etapaVar + 1} de ${variaveis.length} · ${
                  modelo.nome
                }`}
              </Typography>
              <Typography
                variant="h6"
                style={{ fontWeight: 600, marginBottom: 4 }}
              >
                {variaveis[etapaVar].rotulo}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Este valor será inserido no documento de todos os colaboradores
                desta geração.
              </Typography>
              <TextField
                key={variaveis[etapaVar].chave}
                autoFocus
                fullWidth
                multiline={(variaveis[etapaVar].tipo || 'texto') === 'texto'}
                rows={(variaveis[etapaVar].tipo || 'texto') === 'texto' ? 6 : 1}
                type={
                  { numero: 'number', data: 'date', horario: 'time' }[
                    variaveis[etapaVar].tipo
                  ] || 'text'
                }
                variant="outlined"
                margin="normal"
                placeholder={
                  (variaveis[etapaVar].tipo || 'texto') === 'texto'
                    ? 'Digite aqui...'
                    : undefined
                }
                helperText={
                  (variaveis[etapaVar].tipo || 'texto') === 'texto'
                    ? `${
                        (valores[variaveis[etapaVar].chave] || '').length
                      }/${LIMITE_VALOR}`
                    : undefined
                }
                FormHelperTextProps={{ style: { textAlign: 'right' } }}
                value={valores[variaveis[etapaVar].chave] || ''}
                onChange={(e) =>
                  setValores({
                    ...valores,
                    [variaveis[etapaVar].chave]: e.target.value,
                  })
                }
                inputProps={
                  (variaveis[etapaVar].tipo || 'texto') === 'texto'
                    ? { maxLength: LIMITE_VALOR }
                    : variaveis[etapaVar].tipo === 'numero'
                    ? { step: 'any' }
                    : {}
                }
              />
            </Box>
            {comPrevia && (
              <Box flex={1.6} minWidth={0}>
                <Typography variant="caption" color="textSecondary">
                  Prévia (atualiza enquanto você digita)
                </Typography>
                <TextoDoModelo
                  modelo={modelo}
                  valores={valoresNaPrevia}
                  maxHeight={560}
                  zoom={0.7}
                />
              </Box>
            )}
          </Box>
        ) : relatorio ? (
          <>
            <ListaRelatorio
              titulo="Gerados"
              itens={relatorio.gerados}
              severity="success"
            />
            <ListaRelatorio
              titulo="Já existiam"
              itens={relatorio.ja_existiam}
              severity="info"
            />
            <ListaRelatorio
              titulo="Recusados"
              itens={relatorio.recusados}
              severity="error"
            />
          </>
        ) : (
          <>
            {escolheDestinatarios ? (
              <>
                {!soUm && (
                  <Box
                    role="radiogroup"
                    aria-label="Para quem gerar"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: celular ? '1fr' : 'repeat(3, 1fr)',
                      gap: 8,
                    }}
                  >
                    {OPCOES_DESTINO.map(
                      ({ valor, titulo, descricao, Icone }) => {
                        const ativa = abrangencia === valor;
                        return (
                          <div
                            key={valor}
                            role="radio"
                            aria-checked={ativa}
                            tabIndex={0}
                            onClick={() => {
                              if (ativa) return;
                              setAbrangencia(valor);
                              setColaboradores([]);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderRadius: 12,
                              border: `2px solid ${
                                ativa ? PRIMARY_COLOR : '#e2e8f0'
                              }`,
                              backgroundColor: ativa
                                ? `${PRIMARY_COLOR}12`
                                : '#fff',
                            }}
                          >
                            <Icone
                              style={{
                                color: ativa ? PRIMARY_COLOR : '#64748b',
                              }}
                            />
                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="subtitle2"
                                style={{
                                  fontWeight: 700,
                                  color: ativa ? PRIMARY_COLOR : '#0f172a',
                                }}
                              >
                                {titulo}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                {descricao}
                              </Typography>
                            </Box>
                            {ativa ? (
                              <RadioButtonCheckedIcon
                                style={{ color: PRIMARY_COLOR }}
                              />
                            ) : (
                              <RadioButtonUncheckedIcon
                                style={{ color: '#cbd5e1' }}
                              />
                            )}
                          </div>
                        );
                      },
                    )}
                  </Box>
                )}
                {soUm && !colaboradorFixo && (
                  <Alert severity="info">
                    {arquivoObrigatorio
                      ? 'Escolha o colaborador e envie o arquivo dele (PDF ou imagem): o documento é gerado para um colaborador por vez.'
                      : 'Modelo com variáveis: o documento é gerado para um colaborador por vez.'}
                  </Alert>
                )}
                {colaboradorFixo && (
                  <Typography variant="body2">
                    Colaborador: <strong>{colaboradorFixo.nome}</strong>
                  </Typography>
                )}
                {todos && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 8 }}
                  >
                    Gera um documento para cada colaborador ativo.
                  </Typography>
                )}
                {abrangencia === 'varios' && (
                  <Box mt={2}>
                    <FiltrosTabela
                      campos={CAMPOS_FILTRO_GERACAO}
                      valores={filtros}
                      onChange={setFiltros}
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      style={{ marginTop: 4 }}
                    >
                      O documento vai para todos os colaboradores ativos que
                      batem nos filtros.
                    </Typography>
                  </Box>
                )}
                {abrangencia === 'um' && !colaboradorFixo && (
                  <Autocomplete
                    multiple
                    options={opcoes}
                    value={colaboradores}
                    getOptionLabel={(option) => option.nome}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, valor) =>
                      setColaboradores(valor.slice(-1))
                    }
                    onInputChange={(event, valor) => setTermo(valor)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        margin="normal"
                        label="Colaborador"
                      />
                    )}
                  />
                )}
                {comPendencia.length > 0 && (
                  <Alert severity="warning" style={{ marginTop: 8 }}>
                    Já existe documento pendente deste modelo para:{' '}
                    <strong>{comPendencia.join(', ')}</strong>. Se continuar,
                    serão gerados novos documentos.
                  </Alert>
                )}
                {podeTrocarPdf && (
                  <Box mt={1}>
                    {!arquivoObrigatorio && (
                      <OpcaoLinha
                        icone={<PictureAsPdfIcon color="primary" />}
                        titulo="Usar outro arquivo"
                        descricao="Substitui o arquivo do modelo (PDF ou imagem) só para este colaborador"
                        controle={
                          <Switch
                            color="primary"
                            checked={trocarPdf}
                            onChange={(event) => {
                              setTrocarPdf(event.target.checked);
                              setArquivo(null);
                            }}
                          />
                        }
                      />
                    )}
                    {(trocarPdf || arquivoObrigatorio) && (
                      <Box
                        display="flex"
                        alignItems="center"
                        mt={1}
                        style={{ gap: 12 }}
                      >
                        <Button
                          size="small"
                          color="primary"
                          component="label"
                          variant={arquivo ? 'outlined' : 'contained'}
                          startIcon={<CloudUploadIcon />}
                        >
                          {arquivo ? 'Trocar' : 'Selecionar PDF'}
                          <input
                            hidden
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                            onChange={handleArquivo}
                          />
                        </Button>
                        <Typography variant="body2" noWrap>
                          {arquivo
                            ? arquivo.name
                            : 'Nenhum arquivo selecionado'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                Os destinatários são os definidos no modelo. Documentos que já
                existem para o período atual não são duplicados.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      {!relatorio && (
        <DialogActions
          style={
            celular
              ? {
                  // Celular: botões do mesmo tamanho, um ao lado do outro.
                  display: 'grid',
                  gridTemplateColumns: `repeat(${
                    1 +
                    (etapaVar !== null ? 1 : 0) +
                    (modelo && modelo.origem === 'texto' && variaveis.length > 0
                      ? 1
                      : 0)
                  }, 1fr)`,
                  gap: 8,
                  padding: '10px 16px calc(10px + env(safe-area-inset-bottom))',
                }
              : undefined
          }
        >
          {etapaVar !== null && (
            <>
              <Button
                disabled={gerando}
                style={celular ? { minHeight: 48, margin: 0 } : undefined}
                onClick={() =>
                  setEtapaVar(etapaVar === 0 ? null : etapaVar - 1)
                }
              >
                Voltar
              </Button>
              {!celular && <Box flex={1} />}
            </>
          )}
          {modelo && modelo.origem === 'texto' && variaveis.length > 0 && (
            <Button
              color="primary"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              style={celular ? { minHeight: 48, margin: 0 } : undefined}
              onClick={handlePrevia}
              disabled={gerando || gerandoPrevia}
            >
              {gerandoPrevia ? 'Gerando...' : 'Prévia'}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            style={celular ? { minHeight: 48, margin: 0 } : undefined}
            startIcon={etapaVar === null || ultimaEtapa ? <SendIcon /> : null}
            onClick={() => {
              if (etapaVar === null && precisaVariaveis) setEtapaVar(0);
              else if (etapaVar !== null && !ultimaEtapa)
                setEtapaVar(etapaVar + 1);
              else handleGerar();
            }}
            disabled={
              gerando ||
              semDestinatario ||
              faltaArquivo ||
              (etapaVar !== null &&
                !(valores[variaveis[etapaVar].chave] || '').trim())
            }
          >
            {gerando ? 'Gerando...' : rotuloBotao}
          </Button>
        </DialogActions>
      )}
      <VisualizadorPdf
        url={previaUrl}
        titulo={(modelo && modelo.nome) || 'Prévia do documento'}
        subtitulo={
          colaboradores.length === 1
            ? `Prévia com os dados de ${colaboradores[0].nome} · o que ainda não foi preenchido aparece entre colchetes`
            : 'Prévia com dados de exemplo · o que ainda não foi preenchido aparece entre colchetes'
        }
        onClose={fecharPrevia}
      />
    </Dialog>
  );
};

export default DialogoGerarDocumentos;
